import React from 'react';
import { motion } from 'framer-motion';
import { TopToolCard } from './TopToolCard';
import { useTools } from '../hooks/useTools';
import { Tool } from '../types';
// Fallback data for when Supabase is empty - matches original Tool interface
const FALLBACK_TOOLS: Tool[] = [{
  id: '1',
  name: 'ChatGPT',
  description: 'The industry standard for conversational AI, capable of writing, coding, and analysis.',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg',
  url: 'https://chat.openai.com',
  category: 'Writing',
  icon: '💬',
  pricing: 'Freemium',
  priceDetails: 'Free, Plus $20/mo',
  span: '2x2',
  featured: true
}, {
  id: '2',
  name: 'Midjourney',
  description: 'Create stunning, photorealistic images from text descriptions via Discord.',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/e/ed/Midjourney_Emblem.png',
  url: 'https://midjourney.com',
  category: 'Image',
  icon: '🎨',
  pricing: 'Paid',
  priceDetails: 'Basic $10/mo, Standard $30/mo, Pro $60/mo',
  span: '1x2',
  featured: true
}, {
  id: '3',
  name: 'Claude',
  description: "Anthropic's advanced model with massive context window and nuanced understanding.",
  logo: 'https://upload.wikimedia.org/wikipedia/commons/7/77/Anthropic_logo.svg',
  url: 'https://claude.ai',
  category: 'Writing',
  icon: '🤖',
  pricing: 'Freemium',
  priceDetails: 'Free, Pro $20/mo',
  version: '3.5',
  span: '1x2',
  featured: true
}, {
  id: '4',
  name: 'Runway',
  description: 'Professional video generation and editing tools for creative storytellers.',
  logo: 'https://images.crunchbase.com/image/upload/c_lpad,f_auto,q_auto:eco,dpr_1/v14/g3/p/w/g3pwq1q1q1q1q1q1q1.png',
  url: 'https://runwayml.com',
  category: 'Video',
  icon: '🎬',
  pricing: 'Freemium',
  priceDetails: 'Free, Standard $12/mo, Pro $28/mo',
  span: '2x1',
  featured: true
}, {
  id: '5',
  name: 'Gemini',
  description: "Google's multimodal AI model integrated with the Workspace ecosystem.",
  logo: 'https://upload.wikimedia.org/wikipedia/commons/8/8a/Google_Gemini_logo.svg',
  url: 'https://gemini.google.com',
  category: 'Writing',
  icon: '✨',
  pricing: 'Freemium',
  priceDetails: 'Free, Advanced $20/mo',
  span: '1x1',
  featured: true
}, {
  id: '6',
  name: 'Copilot',
  description: "Microsoft's AI companion for coding, writing, and web search.",
  logo: 'https://upload.wikimedia.org/wikipedia/commons/2/2a/Microsoft_365_Copilot_Icon.svg',
  url: 'https://copilot.microsoft.com',
  category: 'Development',
  icon: '👨‍💻',
  pricing: 'Freemium',
  priceDetails: 'Free, Pro $20/mo',
  span: '1x1',
  featured: true
}];
export function TopToolsSection() {
  const {
    tools,
    loading
  } = useTools();
  // Use fetched tools if available, otherwise fallback
  // In a real app with Supabase connected, we'd map database tools to the UI format
  const displayTools = tools.length > 0 ? tools.filter(t => t.is_featured).map(dbTool => ({
    id: dbTool.id,
    name: dbTool.name,
    description: dbTool.description,
    logo: dbTool.logo_url,
    url: dbTool.website_url,
    category: dbTool.category as any,
    icon: '🔧',
    pricing: 'Freemium' as const,
    priceDetails: dbTool.pricing_tiers?.map((t: any) => `${t.name} ${t.price}`).join(', '),
    span: (dbTool.featured_span || '1x1') as any,
    featured: true
  })) : FALLBACK_TOOLS;
  return <section className="py-16 px-4 max-w-7xl mx-auto" data-id="element-346">
      <div className="flex items-end justify-between mb-10" data-id="element-347">
        <div data-id="element-348">
          <h2 className="text-3xl md:text-4xl text-white mb-3 font-subtitle" data-id="element-349">
            Most Popular Tools
          </h2>
          <p className="text-gray-400 text-sm" data-id="element-350">
            The most-used AI tools right now, sized by popularity.
          </p>
        </div>
      </div>

      {/* Masonry/Tetris Grid Layout - Full width on mobile, grid on desktop */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-auto" data-id="element-351">
        {displayTools.map((tool, index) => {
        // Define grid placement based on span - only applies on desktop
        const getGridClass = (span: string) => {
          switch (span) {
            case '2x2':
              return 'md:col-span-2 md:row-span-2';
            // Large square (ChatGPT)
            case '1x2':
              return 'md:col-span-1 md:row-span-2';
            // Tall (Midjourney, Claude)
            case '2x1':
              return 'md:col-span-2 md:row-span-1';
            // Wide (Runway)
            default:
              return 'md:col-span-1 md:row-span-1';
            // Standard (Gemini, Copilot)
          }
        };
        return <motion.div key={tool.id} initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: index * 0.1,
          ease: 'easeOut'
        }} className={getGridClass(tool.span || '1x1')} data-id="element-352">
              <TopToolCard tool={tool} data-id="element-353" />
            </motion.div>;
      })}
      </div>
    </section>;
}